import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowUpRight,
  Circle,
  CopyPlus,
  Hand,
  Image as ImageIcon,
  ImageDown,
  Loader2,
  Lock,
  LockOpen,
  Maximize2,
  MousePointer2,
  PersonStanding,
  Radio,
  Square,
  StickyNote,
  Trash2,
  Type,
  Vote as VoteIcon,
  WifiOff,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { usePoll } from '@/hooks/use-realtime'
import { displayName, initials } from '@/lib/format'
import type { BoardObject, BoardObjectKind, Board, BoardVote, BoardVoteDot, NoteColor, TeamMember } from '@/lib/types'
import { boundsOf, normalizeRect, objectsInMarquee, type Rect } from '@/lib/board-select'
import { drawSelection, loadBoardImages, rasterBounds } from '@/lib/board-raster'
import { copyCanvas, imageFilename, type CopyOutcome } from '@/lib/clipboard-image'
import {
  fitDisplaySize,
  imagePathOf,
  imageRejection,
  pickImageFile,
  storagePath,
} from '@/lib/board-images'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ErrorNote, Loading } from '@/components/page'
import { StartVoteDialog, VoteBanner, isVoteOpen } from '@/components/board-vote'

const COLORS: NoteColor[] = ['yellow', 'blue', 'green', 'pink', 'purple', 'orange', 'gray']

const TOOLS = [
  { kind: 'select' as const, icon: MousePointer2, label: 'Select', key: 'V', hint: 'drag to lasso several' },
  { kind: 'pan' as const, icon: Hand, label: 'Pan', key: 'H' },
  { kind: 'note' as const, icon: StickyNote, label: 'Sticky note', key: 'N' },
  { kind: 'text' as const, icon: Type, label: 'Text', key: 'T' },
  { kind: 'rect' as const, icon: Square, label: 'Rectangle', key: 'R' },
  { kind: 'ellipse' as const, icon: Circle, label: 'Ellipse', key: 'O' },
  { kind: 'arrow' as const, icon: ArrowUpRight, label: 'Connector', key: 'A', hint: 'drag shape to shape' },
  { kind: 'frame' as const, icon: Maximize2, label: 'Frame', key: 'F' },
  { kind: 'meeple' as const, icon: PersonStanding, label: 'Meeple', key: 'M', hint: 'a named person' },
  // Only in the rail while a vote is running — see the filter where TOOLS is
  // rendered. It is a tool rather than a mode switch so that panning, zooming
  // and reading the board all still work mid-vote.
  { kind: 'ping' as const, icon: VoteIcon, label: 'Drop a vote', key: 'P', hint: 'click to place a dot' },
]
type Tool = (typeof TOOLS)[number]['kind']

const DEFAULT_SIZE: Record<BoardObjectKind, { width: number; height: number }> = {
  note: { width: 180, height: 180 },
  text: { width: 240, height: 48 },
  rect: { width: 220, height: 140 },
  ellipse: { width: 180, height: 180 },
  arrow: { width: 180, height: 0 },
  frame: { width: 520, height: 380 },
  // Portrait, and small: a meeple is a token you drop a dozen of, not a shape
  // you lay out one of. The name renders just below this box, so the box is the
  // figure alone.
  meeple: { width: 76, height: 104 },
  // Only a fallback. A pasted image lands at its own shape, shrunk to fit —
  // see fitDisplaySize in lib/board-images.ts — and this size is what an image
  // row with no measurable dimensions gets.
  image: { width: 320, height: 240 },
}

/**
 * How long a signed image URL lasts.
 *
 * Two hours is long enough that nobody watches an image expire mid-workshop,
 * and short enough that a URL copied out of the developer tools stops working
 * the same day. The board re-signs on every load, so the only cost of a shorter
 * window is more round trips.
 */
const IMAGE_URL_TTL_SECONDS = 2 * 60 * 60

const MIN_ZOOM = 0.2
const MAX_ZOOM = 3

/**
 * Copy / paste of board items.
 *
 * ## What is copied
 *
 * The paintable fields only — kind, size, rotation, text, colour, font size and
 * `extra`. Deliberately NOT copied:
 *
 *   - `id`, `board_id`, `team_id`, `created_by` — the insert and its trigger
 *     (`app.board_objects_fill`) own those. team_id in particular is derived
 *     from the board server-side, which is what makes pasting into a different
 *     board safe: paste into another team's board and RLS refuses it.
 *   - `z` — a pasted item belongs on top, so it takes `nextZ()`.
 *   - `from_object_id` / `to_object_id` — see the note on connectors below.
 *
 * ## Why localStorage rather than a ref
 *
 * So you can copy on one board and paste on another. That is the case worth
 * supporting in a workshop — lifting a column of stickies from last month's
 * retro into this one — and a ref dies the moment the route changes. Per-viewer
 * and per-origin, so nothing here crosses to another person.
 *
 * ## Why not the system clipboard
 *
 * `navigator.clipboard.readText()` needs permission in some browsers and
 * returns a promise that a keydown handler cannot await before the browser's
 * own paste runs. Reading our own storage is synchronous and cannot fail
 * halfway. The cost is that copying a sticky here does not put its text on the
 * system clipboard — text inside an item being edited still copies natively,
 * because the handler ignores typing targets.
 */
const CLIPBOARD_KEY = 'teamaccel-board-clipboard'

/** How far a paste lands from the original when the pointer is off-canvas. */
const PASTE_OFFSET = 24

/**
 * The pixel dimensions of an image file.
 *
 * `createImageBitmap` is the direct way to ask, and Safari has supported it
 * since 15. The <img> fallback is for anything older, and the zero-by-zero
 * resolve is for a file the browser cannot decode at all — fitDisplaySize
 * turns that into a visible square rather than a zero-sized object nobody can
 * click on to delete.
 */
async function naturalSize(file: Blob): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file)
      const size = { width: bitmap.width, height: bitmap.height }
      bitmap.close?.()
      return size
    } catch {
      // Fall through to the <img> path.
    }
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      URL.revokeObjectURL(url)
    }
    img.onerror = () => {
      resolve({ width: 0, height: 0 })
      URL.revokeObjectURL(url)
    }
    img.src = url
  })
}

type ClipboardItem = Pick<
  BoardObject,
  'kind' | 'width' | 'height' | 'rotation' | 'text' | 'color' | 'font_size' | 'extra'
>

/**
 * What a copy holds: the items, each with its offset from the group's top-left.
 *
 * The offsets are the whole point of storing a group rather than a list. A
 * pasted selection has to keep its shape — three notes in a row paste as three
 * notes in a row — and absolute coordinates could not survive a paste into a
 * different board, or at a different place on this one.
 */
interface ClipboardGroup {
  items: Array<ClipboardItem & { dx: number; dy: number }>
  width: number
  height: number
}

function toItem(o: BoardObject): ClipboardItem {
  return {
    kind: o.kind,
    width: o.width,
    height: o.height,
    rotation: o.rotation,
    text: o.text,
    color: o.color,
    font_size: o.font_size,
    extra: o.extra,
  }
}

function writeClipboard(objects: BoardObject[]) {
  const bounds = boundsOf(objects)
  if (!bounds) return
  const group: ClipboardGroup = {
    width: bounds.width,
    height: bounds.height,
    items: objects.map((o) => ({ ...toItem(o), dx: o.x - bounds.x, dy: o.y - bounds.y })),
  }
  try {
    localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(group))
  } catch {
    // Private windows and blocked site data. Copy silently does nothing rather
    // than throwing inside a keydown handler.
  }
}

function readClipboard(): ClipboardGroup | null {
  try {
    const raw = localStorage.getItem(CLIPBOARD_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ClipboardGroup | ClipboardItem
    // A single item is what versions before group copy wrote, and it is still
    // in people's localStorage. Read it as a group of one rather than throwing
    // away a copy someone made a minute ago.
    const group: ClipboardGroup =
      'items' in parsed
        ? parsed
        : { items: [{ ...parsed, dx: 0, dy: 0 }], width: parsed.width, height: parsed.height }
    // Written by a previous version of this app, or hand-edited. A bad `kind`
    // would be refused by the CHECK constraint as a 400 the user cannot act on.
    const items = group.items.filter((i) => DEFAULT_SIZE[i.kind])
    return items.length > 0 ? { ...group, items } : null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Connector geometry.
//
// A connected arrow stores no useful coordinates of its own — it stores the two
// shapes it joins, and its line is recomputed from their live geometry on every
// render. That's what makes it follow them as they move, including mid-drag and
// including when the person dragging is someone else on the board.
// ---------------------------------------------------------------------------

/** Arrows can't be endpoints — connecting a connector to a connector. */
function isConnectable(o: BoardObject) {
  return o.kind !== 'arrow'
}

function centerOf(o: Pick<BoardObject, 'x' | 'y' | 'width' | 'height'>) {
  return { x: o.x + o.width / 2, y: o.y + o.height / 2 }
}

/**
 * Topmost connectable object containing `p`, or null.
 *
 * Frames sort last no matter their z: they're backdrops that usually enclose
 * the very shapes you're trying to connect, so a frame winning the hit test
 * would make the shapes inside it unclickable.
 */
function objectAt(objects: BoardObject[], p: { x: number; y: number }, excludeId?: string) {
  const candidates = objects
    .filter((o) => isConnectable(o) && o.id !== excludeId)
    .sort((a, b) => (a.kind === 'frame' ? 1 : 0) - (b.kind === 'frame' ? 1 : 0) || b.z - a.z)

  for (const o of candidates) {
    if (o.kind === 'ellipse') {
      const rx = o.width / 2
      const ry = o.height / 2
      if (rx <= 0 || ry <= 0) continue
      const c = centerOf(o)
      const nx = (p.x - c.x) / rx
      const ny = (p.y - c.y) / ry
      if (nx * nx + ny * ny <= 1) return o
    } else if (p.x >= o.x && p.x <= o.x + o.width && p.y >= o.y && p.y <= o.y + o.height) {
      return o
    }
  }
  return null
}

/**
 * Where the ray from o's center toward `toward` crosses o's border. Connecting
 * centre-to-centre would bury both ends of the line under the shapes; this is
 * what makes the arrow start and stop cleanly at the edges.
 */
function edgePoint(o: BoardObject, toward: { x: number; y: number }) {
  const c = centerOf(o)
  const dx = toward.x - c.x
  const dy = toward.y - c.y
  if (dx === 0 && dy === 0) return c

  if (o.kind === 'ellipse') {
    const rx = Math.max(o.width / 2, 1)
    const ry = Math.max(o.height / 2, 1)
    const t = 1 / Math.hypot(dx / rx, dy / ry)
    return { x: c.x + dx * t, y: c.y + dy * t }
  }

  // Rectangle: scale the direction until it hits whichever side comes first.
  const hw = Math.max(o.width / 2, 1)
  const hh = Math.max(o.height / 2, 1)
  const t = Math.min(hw / Math.abs(dx || 1e-6), hh / Math.abs(dy || 1e-6))
  return { x: c.x + dx * t, y: c.y + dy * t }
}

/** The drawn segment between two shapes, trimmed to both their borders. */
function connectorLine(from: BoardObject, to: BoardObject) {
  return { a: edgePoint(from, centerOf(to)), b: edgePoint(to, centerOf(from)) }
}

/** A live pointer from another person on the board (ephemeral, never stored). */
interface Cursor {
  userId: string
  name: string
  x: number
  y: number
  at: number
}

export function BoardCanvasPage() {
  const { id } = useParams<{ id: string }>()
  const { session, profile, currentTeam, isCoach } = useAuth()
  const userId = session?.user?.id

  const [board, setBoard] = React.useState<Board | null>(null)
  const [objects, setObjects] = React.useState<BoardObject[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const [tool, setTool] = React.useState<Tool>('select')
  const [color, setColor] = React.useState<NoteColor>('yellow')
  // Selection is a LIST, not an id: drag-select, shift-click and group actions
  // all need more than one, and "exactly one" is just the common case of it.
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [view, setView] = React.useState({ x: 0, y: 0, zoom: 1 })
  const [cursors, setCursors] = React.useState<Record<string, Cursor>>({})
  const [live, setLive] = React.useState(false)

  // How many objects the last copy took, purely so the toolbar can say "Copied"
  // for a moment. Ctrl+C is otherwise completely silent, and on a board where
  // the paste may happen on a different screen entirely, silence reads as
  // failure.
  // Signed URLs for the images on this board, keyed by storage path. The
  // bucket is private (0043), so nothing renders until one of these arrives.
  const [imageUrls, setImageUrls] = React.useState<Record<string, string>>({})

  // "Copy as image" is the one board action that can take a visible moment —
  // every image in the selection has to decode first — so it reports being
  // busy, and then reports whether the bytes reached the clipboard or a file.
  const [imaging, setImaging] = React.useState(false)
  const [imaged, setImaged] = React.useState<CopyOutcome | null>(null)
  // A pasted image in flight, and anything the person needs told about a paste
  // that did not work. Both are transient: an upload takes a moment on a slow
  // connection, and silence there is indistinguishable from a broken board.
  const [uploading, setUploading] = React.useState(false)
  const [notice, setNotice] = React.useState<string | null>(null)
  React.useEffect(() => {
    if (!notice) return
    const t = window.setTimeout(() => setNotice(null), 6000)
    return () => window.clearTimeout(t)
  }, [notice])

  // The board bar's title, while somebody is editing it. `null` means nobody
  // is: the bar shows the saved title and a rename is one click away.
  const [renaming, setRenaming] = React.useState<string | null>(null)

  const [copied, setCopied] = React.useState(0)
  React.useEffect(() => {
    if (copied === 0) return
    const t = window.setTimeout(() => setCopied(0), 1200)
    return () => window.clearTimeout(t)
  }, [copied])

  // ---- voting ---------------------------------------------------------------
  // The board's most recent round and its dots. One round at a time is enforced
  // by a partial unique index (0034), so "the latest" is also "the live one"
  // whenever it hasn't closed.
  const [vote, setVote] = React.useState<BoardVote | null>(null)
  const [dots, setDots] = React.useState<BoardVoteDot[]>([])
  const [voteDialog, setVoteDialog] = React.useState(false)
  // user_id -> name, so a dot can say whose it is. Read once per board from the
  // roster rather than joined onto every dot: it is a dozen rows that change
  // about never, against a table that changes every few seconds while a vote
  // runs.
  const [voterNames, setVoterNames] = React.useState<Record<string, string>>({})

  // The in-progress rubber band, in board space. Null when not dragging one.
  const [marquee, setMarquee] = React.useState<{ start: { x: number; y: number }; at: { x: number; y: number } } | null>(null)

  const viewportRef = React.useRef<HTMLDivElement>(null)
  const channelRef = React.useRef<ReturnType<typeof sb>['channel'] extends never ? never : any>(null)

  // Objects being dragged locally. While an id is in here, incoming realtime
  // updates for it are ignored — otherwise a remote echo of a stale position
  // would yank the shape out from under the cursor mid-drag.
  const draggingRef = React.useRef<Set<string>>(new Set())

  // Where every object in the group sat when a group drag began, plus which one
  // the pointer actually grabbed. A drag reports only the grabbed object's new
  // position; the rest move by the same delta, computed from these origins.
  // A ref rather than state because it is read on every pointermove frame.
  const groupDragRef = React.useRef<{
    anchorId: string
    origin: Map<string, { x: number; y: number }>
  } | null>(null)

  // Last pointer position in BOARD space, so a paste lands where the mouse is
  // rather than at a fixed offset. Null until the pointer has been over the
  // canvas — a paste driven purely by the keyboard has no cursor to use.
  const pointerRef = React.useRef<{ x: number; y: number } | null>(null)

  // The window keydown listener is registered with `[selectedIds]` as its only
  // dependency, so anything the handler reads out of state would otherwise be
  // frozen at the render that installed it. Widening the dep array is the wrong
  // fix: `objects` changes on every frame of a drag (patchLocal) and `view` on
  // every frame of a pan, which would mean tearing down and re-adding the
  // listener 60 times a second. Same approach as `targetRef` in pages/present.tsx.
  //
  // `imageUrls` is in here for the same reason, and specifically for
  // copySelectionAsImage: that is reachable from the keyboard, so reading the
  // signed-URL map out of the closure would export whatever URLs had been
  // signed by the render that installed the listener. An image pasted a moment
  // ago gets its URL asynchronously, so the symptom would be an export with a
  // grey rectangle where a screenshot should be — only when copied by ⌘⇧C, and
  // never when copied by the button.
  const latest = React.useRef({ objects, board, view, selectedIds, imageUrls })
  latest.current = { objects, board, view, selectedIds, imageUrls }

  // Bumped on every local write. `load()` captures it before fetching and
  // discards the result if it changed meanwhile — otherwise a poll that was
  // already in flight when you saved comes back with the pre-save rows and
  // overwrites your edit. With realtime down this polls every 2.5s, so the
  // window is wide enough to hit regularly.
  const writeSeq = React.useRef(0)

  // ---- load -----------------------------------------------------------------
  const load = React.useCallback(async () => {
    if (!id) return
    const seq = writeSeq.current
    try {
      const [b, o, v] = await Promise.all([
        sb().from('boards').select('*').eq('id', id).maybeSingle(),
        sb().from('board_objects').select('*').eq('board_id', id).order('z'),
        // Only the newest round. Older ones are history nobody has asked to see
        // yet, and fetching them all would grow without bound on a board a team
        // votes on every week.
        sb()
          .from('board_votes')
          .select('*')
          .eq('board_id', id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])
      if (b.error) throw b.error
      if (o.error) throw o.error
      if (v.error) throw v.error
      // A local write landed while this was in flight, so these rows are already
      // stale. Drop them; the next poll (or the realtime event) brings the
      // merged truth.
      if (writeSeq.current !== seq) return
      setBoard((b.data ?? null) as Board | null)
      setObjects((o.data ?? []) as BoardObject[])

      const round = (v.data ?? null) as BoardVote | null
      setVote(round)
      if (round) {
        const d = await sb().from('board_vote_dots').select('*').eq('vote_id', round.id)
        // A failed dot fetch is not a failed board. Keep whatever dots are on
        // screen rather than blanking the result of a finished vote.
        if (!d.error && writeSeq.current === seq) setDots((d.data ?? []) as BoardVoteDot[])
      } else {
        setDots([])
      }
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => {
    void load()
  }, [load])

  // The roster, once, for naming dots. Failure is silent: an unnamed dot still
  // counts, and a board that refuses to open because a tooltip is missing would
  // be a worse trade.
  React.useEffect(() => {
    const teamId = currentTeam?.id
    if (!teamId) return
    let cancelled = false
    void (async () => {
      const { data } = await sb()
        .from('team_members')
        .select('user_id, email, display_name')
        .eq('team_id', teamId)
      if (cancelled || !data) return
      const map: Record<string, string> = {}
      for (const m of data as TeamMember[]) {
        if (m.user_id) map[m.user_id] = m.display_name || m.email
      }
      setVoterNames(map)
    })()
    return () => {
      cancelled = true
    }
  }, [currentTeam?.id])

  // Polling fallback. When the WebSocket is up this is a slow keep-honest
  // check; when it isn't, it's the only thing making the board collaborative,
  // so it runs often enough to feel shared. Objects mid-drag are excluded from
  // reconciliation by `draggingRef`, so a refetch never fights the pointer.
  usePoll(
    () => {
      if (draggingRef.current.size > 0 || editingId) return
      void load()
    },
    live ? 20_000 : 2_500,
    Boolean(id),
  )

  /**
   * Rename the board.
   *
   * Optimistic, like every other write on this page: the bar shows the new
   * title immediately and the row follows. An empty name is a cancel rather
   * than an error — a board with no name is worse than the one it had, and a
   * validation message in a one-field inline editor is more ceremony than the
   * mistake deserves.
   */
  async function saveTitle(next: string) {
    setRenaming(null)
    const title = next.trim()
    if (!board || !title || title === board.title) return

    const previous = board
    setBoard({ ...board, title })
    writeSeq.current++
    const { error: err } = await sb().from('boards').update({ title }).eq('id', board.id)
    if (err) {
      setBoard(previous)
      setError(err.message)
    }
  }

  // ---- realtime: object changes + presence + live drag ----------------------
  React.useEffect(() => {
    if (!id || !userId) return

    const ch = sb()
      .channel(`board-${id}`, { config: { presence: { key: userId } } })
      // Committed changes from other people.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'board_objects', filter: `board_id=eq.${id}` },
        (payload: any) => {
          const row = (payload.new ?? payload.old) as BoardObject
          if (!row?.id) return
          if (draggingRef.current.has(row.id)) return

          setObjects((prev) => {
            if (payload.eventType === 'DELETE') return prev.filter((o) => o.id !== row.id)
            const next = payload.new as BoardObject
            const idx = prev.findIndex((o) => o.id === next.id)
            if (idx === -1) return [...prev, next]
            const copy = prev.slice()
            copy[idx] = next
            return copy
          })
        },
      )
      // The board row, which is how a rename reaches everyone else on the
      // canvas. `boards` has been in the publication since 0008; nothing here
      // subscribed to it until renaming existed.
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'boards', filter: `id=eq.${id}` },
        (payload: any) => {
          const next = payload.new as Board
          if (next?.id) setBoard(next)
        },
      )
      // In-flight drags, so movement looks continuous rather than snapping on
      // release. Broadcast is ephemeral by design — the DB write on pointerup is
      // still the source of truth.
      .on('broadcast', { event: 'drag' }, ({ payload }: any) => {
        if (payload.by === userId) return
        setObjects((prev) =>
          prev.map((o) =>
            o.id === payload.id && !draggingRef.current.has(o.id)
              ? { ...o, x: payload.x, y: payload.y, width: payload.width ?? o.width, height: payload.height ?? o.height }
              : o,
          ),
        )
      })
      .on('presence', { event: 'sync' }, () => {
        const state = ch.presenceState() as Record<string, Array<{ userId: string; name: string; x: number; y: number }>>
        const next: Record<string, Cursor> = {}
        for (const entries of Object.values(state)) {
          const e = entries[entries.length - 1]
          if (!e || e.userId === userId) continue
          next[e.userId] = { ...e, at: Date.now() }
        }
        setCursors(next)
      })
      .subscribe(async (status) => {
        setLive(status === 'SUBSCRIBED')
        if (status === 'SUBSCRIBED') {
          await ch.track({
            userId,
            name: displayName(profile),
            x: 0,
            y: 0,
          })
        }
      })

    channelRef.current = ch
    return () => {
      channelRef.current = null
      setLive(false)
      void sb().removeChannel(ch)
    }
  }, [id, userId, profile])

  // The clock, ticked only while a round is open. Without it the tool would
  // stay armed after time ran out until the next poll — the database refuses
  // those dots anyway, but being told "this vote has ended" by an alert is a
  // worse way to learn it than watching the timer hit zero.
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    if (!vote || vote.closed_at) return
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [vote])

  const voteOpen = isVoteOpen(vote, now)

  // The window keydown listener closes over the render that installed it, and
  // widening its deps to include `vote` would re-register it every tick.
  const voteRef = React.useRef(vote)
  voteRef.current = vote

  // Everyone lands on the voting tool when a round opens: a vote is a thing the
  // room does together, and hunting for the right tool is how the first thirty
  // seconds get wasted. Keyed on the id so it happens once per round, not on
  // every poll, and Esc still goes back to Select.
  const armedVoteRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (voteOpen && vote && armedVoteRef.current !== vote.id) {
      armedVoteRef.current = vote.id
      setTool('ping')
    }
    if (!voteOpen) setTool((t) => (t === 'ping' ? 'select' : t))
  }, [voteOpen, vote])

  // ---- coordinate helpers ---------------------------------------------------
  /** Screen (client) point → board space. */
  const toBoard = React.useCallback(
    (clientX: number, clientY: number) => {
      const rect = viewportRef.current?.getBoundingClientRect()
      if (!rect) return { x: 0, y: 0 }
      return {
        x: (clientX - rect.left - view.x) / view.zoom,
        y: (clientY - rect.top - view.y) / view.zoom,
      }
    },
    [view],
  )

  /** Middle of what is currently on screen, in board space. Where a paste goes
   *  when the pointer has never been over the canvas. */
  const viewCenter = React.useCallback(() => {
    const rect = viewportRef.current?.getBoundingClientRect()
    if (!rect) return { x: 0, y: 0 }
    // Inlined rather than calling toBoard, which closes over `view`. This is
    // reached from the keydown handler, where that value is stale.
    const v = latest.current.view
    return {
      x: (rect.width / 2 - v.x) / v.zoom,
      y: (rect.height / 2 - v.y) / v.zoom,
    }
  }, [])

  const nextZ = React.useCallback(
    () => (objects.length === 0 ? 0 : Math.max(...objects.map((o) => o.z)) + 1),
    [objects],
  )

  // ---- mutations ------------------------------------------------------------
  async function createObject(kind: BoardObjectKind, at: { x: number; y: number }) {
    if (!id || !board) return
    const size = DEFAULT_SIZE[kind]
    // Drop centered on the click for shapes; arrows read better anchored at the
    // point you clicked.
    const draft = {
      board_id: id,
      team_id: board.team_id,
      kind,
      x: kind === 'arrow' ? at.x : at.x - size.width / 2,
      y: kind === 'arrow' ? at.y : at.y - size.height / 2,
      width: size.width,
      height: size.height,
      color: kind === 'frame' ? 'gray' : color,
      z: nextZ(),
      text: null as string | null,
    }
    writeSeq.current++
    const { data, error: err } = await sb().from('board_objects').insert(draft).select().single()
    writeSeq.current++
    if (err) return window.alert(err.message)
    const created = data as BoardObject
    setObjects((prev) => [...prev, created])
    setSelectedIds([created.id])
    // Sticky notes and text exist to hold words — go straight into editing. So
    // does a meeple: an unnamed person token says nothing, and the name is the
    // only thing about it that isn't already visible.
    if (kind === 'note' || kind === 'text' || kind === 'frame' || kind === 'meeple')
      setEditingId(created.id)

    // The tool deliberately STAYS active. It used to flip back to Select here,
    // which meant the first click placed a shape and every click after it did
    // nothing at all — indistinguishable from the board being broken. Nobody
    // drops exactly one sticky note in a workshop. Esc returns to Select.
  }

  /** Local-only update, for the duration of a drag or resize. */
  function patchLocal(objectId: string, patch: Partial<BoardObject>) {
    setObjects((prev) => prev.map((o) => (o.id === objectId ? { ...o, ...patch } : o)))
  }

  async function commit(objectId: string, patch: Partial<BoardObject>) {
    patchLocal(objectId, patch)
    writeSeq.current++
    const { error: err } = await sb().from('board_objects').update(patch).eq('id', objectId)
    writeSeq.current++
    if (err) {
      window.alert(err.message)
      void load()
    }
  }

  /**
   * Delete everything selected, in one request. Locked items are left where
   * they are and say so — the database would refuse the whole `in` otherwise
   * (0070), and a delete that removes nothing because one item in eight was
   * pinned reads as a broken button.
   */
  async function removeSelection() {
    const selected = selectionObjects()
    const pinned = selected.filter((o) => o.locked).length
    if (pinned > 0) {
      setNotice(
        `${pinned} locked ${pinned === 1 ? 'item was' : 'items were'} not deleted. Unlock ${pinned === 1 ? 'it' : 'them'} first.`,
      )
    }
    const ids = selected.filter((o) => !o.locked).map((o) => o.id)
    if (ids.length === 0) return
    const gone = new Set(ids)
    setObjects((prev) => prev.filter((o) => !gone.has(o.id)))
    setSelectedIds([])
    writeSeq.current++
    // One `in` rather than a delete per object: a partial failure halfway
    // through a loop leaves a selection the user cannot see or undo.
    const { error: err } = await sb().from('board_objects').delete().in('id', ids)
    writeSeq.current++
    if (err) {
      window.alert(err.message)
      void load()
    }
  }

  /**
   * Lock or unlock the selection, in one request. A lock is a guard against a
   * stray drag, not a permission — anyone who can edit the board can lift it
   * (0070) — so the button is never disabled for "someone else's lock".
   * Who locked it and when are stamped by the database, never sent from here.
   */
  async function setLocked(locked: boolean) {
    const ids = selectionObjects()
      .filter((o) => o.locked !== locked)
      .map((o) => o.id)
    if (ids.length === 0) return
    const gone = new Set(ids)
    setObjects((prev) => prev.map((o) => (gone.has(o.id) ? { ...o, locked } : o)))
    // Locking whatever is being edited ends the edit, rather than leaving an
    // open text box on an item that can no longer take the text.
    // Functional update: this is reachable from the keydown listener, whose
    // closure holds a stale editingId.
    if (locked) setEditingId((cur) => (cur && gone.has(cur) ? null : cur))
    writeSeq.current++
    const { error: err } = await sb().from('board_objects').update({ locked }).in('id', ids)
    writeSeq.current++
    if (err) window.alert(err.message)
    // Reload either way: it brings back locked_by / locked_at for the tooltip.
    void load()
  }

  // ---- copy / paste ---------------------------------------------------------
  /**
   * Insert a whole clipboard group at a point, and select what it made.
   *
   * `at` is the group's top-left, already resolved by the caller — paste
   * centres on the cursor, duplicate offsets from the originals. Every item
   * keeps its offset within the group, so the arrangement survives the trip.
   *
   * One INSERT for the whole group rather than one per item: a half-inserted
   * paste is worse than none, and the round trips add up on a big selection.
   */
  async function placeClipboardGroup(group: ClipboardGroup, at: { x: number; y: number }) {
    // Read through `latest` rather than the closure: this runs from the window
    // keydown handler, whose captured state is one render old at best.
    const { objects: live, board: liveBoard } = latest.current
    if (!id || !liveBoard) return
    const baseZ = live.length === 0 ? 0 : Math.max(...live.map((o) => o.z)) + 1
    const drafts = group.items.map((item, i) => ({
      board_id: id,
      // Re-derived from the board by app.board_objects_fill, so this is a
      // formality — and the reason a paste into another team's board is a 403
      // rather than a silent cross-team write.
      team_id: liveBoard.team_id,
      kind: item.kind,
      x: at.x + item.dx,
      y: at.y + item.dy,
      width: item.width,
      height: item.height,
      rotation: item.rotation,
      // Stacked above everything, and in their original order among themselves.
      z: baseZ + i,
      text: item.text,
      color: item.color,
      font_size: item.font_size,
      extra: item.extra,
      // Endpoints are deliberately absent. A connector's geometry is computed
      // from the two shapes it joins (connectorLine), so a copy that kept them
      // would either duplicate an existing connector on top of itself or, on
      // another board, point at ids that are not there. Pasting one gives a
      // free arrow with the same size and colour instead.
    }))
    writeSeq.current++
    const { data, error: err } = await sb().from('board_objects').insert(drafts).select()
    writeSeq.current++
    if (err) return window.alert(err.message)
    const created = (data ?? []) as BoardObject[]
    setObjects((prev) => [...prev, ...created])
    // Select the copies, not the originals: the next drag is nearly always
    // "now put these somewhere".
    setSelectedIds(created.map((o) => o.id))
    // No setEditingId, unlike createObject: the text came with the copy, so
    // opening an editor over it would only be in the way.
  }

  /** The selected objects, read fresh — callers include the keydown handler. */
  function selectionObjects(): BoardObject[] {
    const { objects: live, selectedIds: ids } = latest.current
    const want = new Set(ids)
    return live.filter((o) => want.has(o.id))
  }

  function copySelection() {
    const sel = selectionObjects()
    if (sel.length === 0) return
    writeClipboard(sel)
    setCopied(sel.length)
  }

  /**
   * Copy the selection to the system clipboard as a PNG.
   *
   * Separate from `copySelection`, which writes the OBJECTS so they can be
   * pasted back onto a board. Both are "copy", and they are not
   * interchangeable: one round-trips, the other leaves the app. So ⌘C keeps
   * the objects — losing the ability to paste a note onto another board would
   * be a worse trade — and this takes ⌘⇧C.
   *
   * Drawn from the model rather than from the screen, so the export is at full
   * resolution whatever the board is zoomed to, and carries only what was
   * selected. See the header of `lib/board-raster.ts`.
   */
  async function copySelectionAsImage() {
    const sel = selectionObjects()
    if (sel.length === 0) return
    const bounds = rasterBounds(sel)
    if (!bounds) return

    setImaging(true)
    try {
      // Images have to be decoded before the draw, which is synchronous. The
      // URLs are the signed ones the page already holds — see
      // `loadBoardImages` for why that matters to toBlob.
      const images = await loadBoardImages(sel, (path) => latest.current.imageUrls[path])
      const canvas = drawSelection(sel, bounds, { images })
      if (!canvas) return flashImage('failed')
      flashImage(
        await copyCanvas(
          canvas,
          imageFilename([latest.current.board?.title, 'selection'], 'board'),
        ),
      )
    } catch {
      flashImage('failed')
    } finally {
      setImaging(false)
    }
  }

  function flashImage(outcome: CopyOutcome) {
    setImaged(outcome)
    window.setTimeout(() => setImaged((v) => (v === outcome ? null : v)), 2200)
  }

  async function pasteClipboard() {
    const group = readClipboard()
    if (!group) return
    // Centre on the cursor when there is one. Falling back to the middle of the
    // current view rather than 0,0 means a keyboard-only paste lands somewhere
    // the user is actually looking.
    const p = pointerRef.current ?? viewCenter()
    await placeClipboardGroup(group, { x: p.x - group.width / 2, y: p.y - group.height / 2 })
  }

  // ---- images ---------------------------------------------------------------
  //
  // Every image path on the board that has no URL yet gets one, in a single
  // call. Runs whenever the object list changes, so an image pasted by someone
  // else appears as soon as its row arrives over realtime.
  React.useEffect(() => {
    const missing = [
      ...new Set(
        objects
          .filter((o) => o.kind === 'image')
          .map((o) => imagePathOf(o.extra))
          .filter((p): p is string => Boolean(p)),
      ),
    ].filter((p) => !imageUrls[p])
    if (missing.length === 0) return

    let cancelled = false
    void (async () => {
      const { data, error: err } = await sb()
        .storage.from('board-images')
        .createSignedUrls(missing, IMAGE_URL_TTL_SECONDS)
      if (cancelled || err || !data) return
      setImageUrls((prev) => {
        const next = { ...prev }
        for (const row of data) {
          // A path that no longer exists in the bucket comes back with an error
          // and no URL. Leaving it unset is right: the object renders as a
          // placeholder rather than as a broken image icon.
          if (row.path && row.signedUrl) next[row.path] = row.signedUrl
        }
        return next
      })
    })()
    return () => {
      cancelled = true
    }
  }, [objects, imageUrls])

  /**
   * Put a pasted image on the board.
   *
   * Upload first, row second. The other order would need a row that points at
   * bytes which may never arrive, and a board object whose image 404s forever
   * is worse than a paste that failed loudly.
   */
  async function placeImage(file: File) {
    // Through `latest`, not the closure. This runs from the window paste
    // handler, whose effect is re-registered only when the selection changes —
    // so the `board` captured there is null for anyone who opens a board and
    // pastes before clicking anything, and the paste would do nothing at all.
    const liveBoard = latest.current.board
    if (!id || !liveBoard) return
    const rejection = imageRejection(file)
    if (rejection) return setNotice(rejection)

    setUploading(true)
    try {
      // Measure it before it goes anywhere, so it lands at its own shape.
      const natural = await naturalSize(file)
      const path = storagePath(liveBoard.team_id, id, file.type, crypto.randomUUID())
      const { error: upErr } = await sb()
        .storage.from('board-images')
        .upload(path, file, { contentType: file.type })
      if (upErr) return setNotice(`That image did not upload: ${upErr.message}`)

      const size = fitDisplaySize(natural.width, natural.height)
      const p = pointerRef.current ?? viewCenter()
      writeSeq.current++
      const { data, error: err } = await sb()
        .from('board_objects')
        .insert({
          board_id: id,
          team_id: liveBoard.team_id,
          kind: 'image',
          x: p.x - size.width / 2,
          y: p.y - size.height / 2,
          width: size.width,
          height: size.height,
          z: nextZ(),
          extra: {
            path,
            mime: file.type,
            bytes: file.size,
            naturalWidth: natural.width,
            naturalHeight: natural.height,
          },
        })
        .select()
        .single()
      writeSeq.current++
      if (err) {
        // The row failed but the bytes are already stored. Take them back out
        // rather than leaving a file nothing will ever reference.
        await sb().storage.from('board-images').remove([path])
        return setNotice(err.message)
      }
      const created = data as BoardObject
      setObjects((prev) => [...prev, created])
      setSelectedIds([created.id])
    } finally {
      setUploading(false)
    }
  }

  async function duplicateSelection() {
    const sel = selectionObjects()
    if (sel.length === 0) return
    const bounds = boundsOf(sel)
    if (!bounds) return
    writeClipboard(sel)
    setCopied(sel.length)
    // Offset rather than centred on the cursor: duplicate means "another one of
    // these, next to it", and landing exactly on top would look like nothing
    // happened.
    await placeClipboardGroup(
      {
        width: bounds.width,
        height: bounds.height,
        items: sel.map((o) => ({ ...toItem(o), dx: o.x - bounds.x, dy: o.y - bounds.y })),
      },
      { x: bounds.x + PASTE_OFFSET, y: bounds.y + PASTE_OFFSET },
    )
  }

  // ---- voting ---------------------------------------------------------------
  async function startVote(opts: { label: string | null; votesPerUser: number; minutes: number }) {
    if (!id || !board) return
    const { data, error: err } = await sb()
      .from('board_votes')
      .insert({
        board_id: id,
        // Re-derived by app.board_votes_fill; sent for the same reason as
        // everywhere else on this page — the 403 on a wrong team is explicit.
        team_id: board.team_id,
        label: opts.label,
        votes_per_user: opts.votesPerUser,
        ends_at: new Date(Date.now() + opts.minutes * 60_000).toISOString(),
      })
      .select()
      .single()
    if (err) {
      // The partial unique index (0034) is what a second coach clicking Start
      // at the same moment hits. Say so rather than showing a constraint name.
      window.alert(
        err.code === '23505' ? 'A vote is already running on this board.' : err.message,
      )
      return
    }
    setVote(data as BoardVote)
    setDots([])
    setVoteDialog(false)
    setTool('ping')
  }

  async function endVote() {
    if (!vote) return
    const { error: err } = await sb()
      .from('board_votes')
      .update({ closed_at: new Date().toISOString() })
      .eq('id', vote.id)
    if (err) return window.alert(err.message)
    setVote((v) => (v ? { ...v, closed_at: new Date().toISOString() } : v))
    setTool('select')
  }

  /** Remove the round and its dots. The cascade in 0034 takes the dots. */
  async function clearVote() {
    if (!vote) return
    if (!window.confirm('Clear this vote and all of its dots?')) return
    const { error: err } = await sb().from('board_votes').delete().eq('id', vote.id)
    if (err) return window.alert(err.message)
    setVote(null)
    setDots([])
  }

  async function dropDot(at: { x: number; y: number }) {
    if (!id || !vote || !userId) return
    // Attribute the dot with the same hit test a click uses, so a dot that
    // looks like it landed on a sticky is recorded against that sticky.
    const hit = objectAt(objects, at)
    const { data, error: err } = await sb()
      .from('board_vote_dots')
      .insert({
        vote_id: vote.id,
        board_id: id,
        team_id: vote.team_id,
        user_id: userId,
        x: at.x,
        y: at.y,
        object_id: hit?.id ?? null,
      })
      .select()
      .single()
    if (err) {
      // The trigger's own sentences — "you have used all 3 of your votes",
      // "this vote has ended" — are written to be read by a person, so they go
      // straight through instead of being replaced with something vaguer.
      window.alert(err.message)
      void load()
      return
    }
    setDots((prev) => [...prev, data as BoardVoteDot])
  }

  async function removeDot(dotId: string) {
    setDots((prev) => prev.filter((d) => d.id !== dotId))
    const { error: err } = await sb().from('board_vote_dots').delete().eq('id', dotId)
    if (err) {
      window.alert(err.message)
      void load()
    }
  }

  function broadcastDrag(o: BoardObject) {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'drag',
      payload: { id: o.id, x: o.x, y: o.y, width: o.width, height: o.height, by: userId },
    })
  }

  /**
   * Join two shapes with an arrow.
   *
   * x/y/width/height are NOT NULL in the schema, so we store the line's current
   * geometry as a snapshot. Nothing reads it while both endpoints exist — the
   * line is recomputed from live shape positions every render — but it keeps
   * the row valid and gives the arrow sane coordinates to fall back on.
   */
  async function connectObjects(fromId: string, toId: string) {
    if (!id || !board || fromId === toId) return
    const from = objects.find((o) => o.id === fromId)
    const to = objects.find((o) => o.id === toId)
    if (!from || !to) return

    // Don't stack a second identical arrow when someone re-drags the same pair.
    const existing = objects.find(
      (o) => o.kind === 'arrow' && o.from_object_id === fromId && o.to_object_id === toId,
    )
    if (existing) {
      setSelectedIds([existing.id])
      return
    }

    const { a, b } = connectorLine(from, to)
    const draft = {
      board_id: id,
      team_id: board.team_id,
      kind: 'arrow' as const,
      x: a.x,
      y: a.y,
      width: b.x - a.x,
      height: b.y - a.y,
      color,
      z: nextZ(),
      from_object_id: fromId,
      to_object_id: toId,
    }
    writeSeq.current++
    const { data, error: err } = await sb().from('board_objects').insert(draft).select().single()
    writeSeq.current++
    if (err) return window.alert(err.message)
    const created = data as BoardObject
    setObjects((prev) => [...prev, created])
    setSelectedIds([created.id])
  }

  /** Free-floating arrow drawn by dragging across empty canvas. */
  async function createFreeArrow(a: { x: number; y: number }, b: { x: number; y: number }) {
    if (!id || !board) return
    const draft = {
      board_id: id,
      team_id: board.team_id,
      kind: 'arrow' as const,
      x: a.x,
      y: a.y,
      width: b.x - a.x,
      height: b.y - a.y,
      color,
      z: nextZ(),
    }
    writeSeq.current++
    const { data, error: err } = await sb().from('board_objects').insert(draft).select().single()
    writeSeq.current++
    if (err) return window.alert(err.message)
    const created = data as BoardObject
    setObjects((prev) => [...prev, created])
    setSelectedIds([created.id])
  }

  // ---- pointer interactions -------------------------------------------------
  const panState = React.useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null)
  const spaceDown = React.useRef(false)

  /**
   * In-progress arrow drag. `fromId` is set when the drag started on a shape
   * (it will become a connector); null means it started on empty canvas and
   * will become a free-floating arrow.
   */
  const [drawing, setDrawing] = React.useState<{
    fromId: string | null
    start: { x: number; y: number }
    at: { x: number; y: number }
    targetId: string | null
  } | null>(null)

  function onViewportPointerDown(e: React.PointerEvent) {
    // Only react to the primary button; right-click is for the browser menu.
    if (e.button !== 0 && e.button !== 1) return

    const wantsPan = tool === 'pan' || spaceDown.current || e.button === 1
    if (wantsPan) {
      panState.current = { startX: e.clientX, startY: e.clientY, originX: view.x, originY: view.y }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      return
    }

    // Deliberately NOT short-circuiting when a note is mid-edit. With the tool
    // armed, one click should place one shape — needing a throwaway click to
    // dismiss the editor first makes every second click do nothing, which is
    // the same complaint this whole change exists to fix.
    //
    // The in-progress text is safe: placing the next shape moves `editingId`,
    // which unmounts the open editor, and TextEditor commits from its effect
    // cleanup precisely so an unmount can't drop what you typed.

    // The arrow tool draws rather than drops: press on a shape and drag to
    // another to join them, or drag across empty canvas for a loose arrow.
    if (tool === 'arrow') {
      const p = toBoard(e.clientX, e.clientY)
      const hit = objectAt(objects, p)
      setDrawing({ fromId: hit?.id ?? null, start: hit ? centerOf(hit) : p, at: p, targetId: null })
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      return
    }

    if (tool === 'ping') {
      // Dots are the one thing on this canvas that a closed round must refuse
      // locally too: the insert would fail anyway, but an alert is a poor way
      // to say "the vote is over".
      if (voteOpen) void dropDot(toBoard(e.clientX, e.clientY))
      return
    }

    if (tool !== 'select') {
      void createObject(tool as BoardObjectKind, toBoard(e.clientX, e.clientY))
      return
    }

    // Select tool on bare canvas: begin a rubber band. A press that never moves
    // ends as a plain click and clears the selection (see pointer-up), so this
    // costs nothing when someone just wanted to deselect.
    //
    // Deliberately does NOT clear `editingId`: the click blurs the open text
    // editor, whose blur handler commits and then closes it. Clearing it here
    // would unmount the editor first and race its own save.
    const p = toBoard(e.clientX, e.clientY)
    setMarquee({ start: p, at: p })
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    // Shift keeps what is already selected, so a second lasso adds to the first.
    if (!e.shiftKey && !e.metaKey) setSelectedIds([])
  }

  function onViewportPointerMove(e: React.PointerEvent) {
    if (panState.current) {
      setView((v) => ({
        ...v,
        x: panState.current!.originX + (e.clientX - panState.current!.startX),
        y: panState.current!.originY + (e.clientY - panState.current!.startY),
      }))
      return
    }

    if (marquee) {
      const p = toBoard(e.clientX, e.clientY)
      setMarquee((m) => (m ? { ...m, at: p } : m))
      // Falls through to the cursor broadcast below, so teammates see the lasso
      // being drawn as pointer movement even though the band itself is local.
    }

    if (drawing) {
      const p = toBoard(e.clientX, e.clientY)
      // Only highlight a drop target when the drag actually started on a shape;
      // a loose arrow doesn't snap to anything.
      const target = drawing.fromId ? objectAt(objects, p, drawing.fromId) : null
      setDrawing((d) => (d ? { ...d, at: p, targetId: target?.id ?? null } : d))
      // Fall through to the cursor broadcast — drawing a connector is exactly
      // when teammates most want to see where you are.
    }
    // Share the cursor so others can see where you're working. Presence updates
    // are throttled by the channel's own rate limit, and this only fires while
    // the pointer is actually over the canvas.
    const p = toBoard(e.clientX, e.clientY)
    pointerRef.current = p
    channelRef.current?.track({ userId, name: displayName(profile), x: p.x, y: p.y })
  }

  function onViewportPointerUp(e: React.PointerEvent) {
    if (panState.current) {
      panState.current = null
      ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
    }

    if (marquee) {
      const m = marquee
      setMarquee(null)
      ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
      const rect = normalizeRect(m.start, m.at)
      // Below this, the gesture was a click, not a lasso — and a click on bare
      // canvas means "deselect", which pointer-down already did. The threshold
      // is in board units, so it shrinks with zoom exactly as the pointer's
      // idea of "I didn't move" does.
      if (rect.width * view.zoom >= 4 || rect.height * view.zoom >= 4) {
        const hit = objectsInMarquee(objects, rect)
        setSelectedIds((prev) =>
          e.shiftKey || e.metaKey ? Array.from(new Set([...prev, ...hit])) : hit,
        )
        if (hit.length === 1) {
          const only = objects.find((o) => o.id === hit[0])
          if (only) setColor(only.color)
        }
      }
    }

    if (drawing) {
      const d = drawing
      setDrawing(null)
      ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
      const p = toBoard(e.clientX, e.clientY)

      if (d.fromId) {
        const target = objectAt(objects, p, d.fromId)
        // Released somewhere that isn't another shape: nothing to connect to,
        // so drop the drag rather than leaving a half-attached arrow.
        if (target) void connectObjects(d.fromId, target.id)
      } else if (Math.hypot(p.x - d.start.x, p.y - d.start.y) >= 8) {
        void createFreeArrow(d.start, p)
      } else {
        // A bare click on empty canvas with the arrow tool — keep the old
        // behaviour of dropping a default-length arrow there.
        void createObject('arrow', d.start)
      }
      setTool('select')
    }
  }

  function onWheel(e: React.WheelEvent) {
    // Trackpad pinch arrives as ctrlKey+wheel; plain wheel scrolls the canvas.
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      const rect = viewportRef.current?.getBoundingClientRect()
      if (!rect) return
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top
      setView((v) => {
        const zoom = clamp(v.zoom * Math.exp(-e.deltaY * 0.002), MIN_ZOOM, MAX_ZOOM)
        // Keep the point under the cursor fixed while zooming.
        const k = zoom / v.zoom
        return { zoom, x: px - (px - v.x) * k, y: py - (py - v.y) * k }
      })
    } else {
      setView((v) => ({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }))
    }
  }

  // ---- keyboard -------------------------------------------------------------
  React.useEffect(() => {
    function isTypingTarget(t: EventTarget | null) {
      const el = t as HTMLElement | null
      return Boolean(el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)))
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.code === 'Space' && !isTypingTarget(e.target)) {
        spaceDown.current = true
        return
      }
      if (isTypingTarget(e.target)) return

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault()
        void removeSelection()
        return
      }

      // Copy / cut / paste / duplicate. Checked BEFORE the single-letter tool
      // shortcuts below, which is why those test for a bare key: without this
      // ordering ⌘V would also switch to a tool bound to V.
      //
      // `isTypingTarget` has already returned above, so text selected inside a
      // sticky note still copies natively — these only fire when the canvas
      // itself has focus.
      const mod = e.metaKey || e.ctrlKey
      if (mod && !e.altKey) {
        const some = latest.current.selectedIds.length > 0
        const k = e.key.toLowerCase()
        if (k === 'a') {
          // Select everything the marquee could have reached. Browsers bind this
          // to "select all text", which on a canvas selects the whole page.
          e.preventDefault()
          setSelectedIds(
            latest.current.objects
              .filter((o) => !(o.kind === 'arrow' && o.from_object_id && o.to_object_id))
              .map((o) => o.id),
          )
          return
        }
        if (k === 'c' && some) {
          e.preventDefault()
          // ⇧ takes the picture; plain ⌘C keeps the objects, so a note can
          // still be pasted onto another board. See copySelectionAsImage.
          if (e.shiftKey) void copySelectionAsImage()
          else copySelection()
          return
        }
        if (k === 'x' && some) {
          e.preventDefault()
          copySelection()
          void removeSelection()
          return
        }
        // ⌘V is NOT handled here. It arrives as a `paste` event on the window
        // (registered below), which is the only place the clipboard's CONTENTS
        // are readable — and preventing the default here would stop that event
        // firing at all, so an image paste would silently do nothing.
        if (k === 'l' && e.shiftKey && some) {
          // ⌘⇧L, as in Figma: lock the selection, or unlock it if it is all
          // locked already.
          e.preventDefault()
          void setLocked(!latest.current.objects
            .filter((o) => latest.current.selectedIds.includes(o.id))
            .every((o) => o.locked))
          return
        }
        if (k === 'd' && some) {
          // Browsers bind ⌘D to "bookmark this page", so preventDefault is
          // load-bearing here rather than tidy.
          e.preventDefault()
          void duplicateSelection()
          return
        }
      }
      if (e.key === 'Escape') {
        // Abandons a half-drawn connector or lasso too, so a drag started by
        // mistake doesn't have to be completed somewhere harmless.
        setDrawing(null)
        setMarquee(null)
        setSelectedIds([])
        setEditingId(null)
        setTool('select')
        return
      }
      const match = TOOLS.find((t) => t.key.toLowerCase() === e.key.toLowerCase())
      // P is only a tool while a round is open; outside one it does nothing
      // rather than arming a tool whose clicks the database would refuse.
      if (match && match.kind === 'ping' && !isVoteOpen(voteRef.current, Date.now())) return
      if (match && !e.metaKey && !e.ctrlKey) setTool(match.kind)
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.code === 'Space') spaceDown.current = false
    }
    /**
     * The real paste. One handler for both kinds:
     *
     *   an image in the clipboard  -> upload it and place it
     *   anything else              -> our own copied objects, from localStorage
     *
     * A paste carrying an image also carries text and HTML, so the image is
     * checked first: copying a picture out of a browser and getting its alt
     * text as a sticky note would be a strange kind of correct.
     */
    function onPaste(e: ClipboardEvent) {
      if (isTypingTarget(e.target)) return
      const file = pickImageFile(Array.from(e.clipboardData?.files ?? []))
      if (file) {
        e.preventDefault()
        void placeImage(file)
        return
      }
      void pasteClipboard()
    }

    window.addEventListener('paste', onPaste)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('paste', onPaste)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds])

  // ---- render ---------------------------------------------------------------
  // A lock that arrives from another person (via the poll) while this person is
  // editing that item closes the editor; onEndEdit then reports the unsaved
  // text rather than sending a write the database would refuse.
  React.useEffect(() => {
    if (editingId && objects.find((o) => o.id === editingId)?.locked) setEditingId(null)
  }, [objects, editingId])

  if (loading) return <Loading label="Opening board…" />
  if (error) {
    return (
      <div className="mx-auto max-w-lg p-6">
        <ErrorNote error={error} />
      </div>
    )
  }
  if (!board) {
    return (
      <div className="mx-auto max-w-lg p-6 text-center">
        <p className="mb-4 text-sm text-muted-foreground">That board doesn&apos;t exist, or isn&apos;t yours.</p>
        <Button variant="outline" asChild>
          <Link to="/boards">
            <ArrowLeft /> Back to boards
          </Link>
        </Button>
      </div>
    )
  }

  // Dots per object, biggest first. Only objects that got at least one.
  const tallies = (() => {
    const counts = new Map<string, number>()
    for (const d of dots) {
      if (d.object_id) counts.set(d.object_id, (counts.get(d.object_id) ?? 0) + 1)
    }
    return [...counts.entries()]
      .map(([objectId, count]) => ({ object: objects.find((o) => o.id === objectId), count }))
      .filter((t): t is { object: BoardObject; count: number } => Boolean(t.object))
      .sort((a, b) => b.count - a.count)
  })()

  const selectedSet = new Set(selectedIds)
  const selectedObjects = objects.filter((o) => selectedSet.has(o.id))
  // The lock button reads as "Unlock" only when EVERYTHING selected is locked,
  // so a mixed selection locks the rest rather than unlocking the pinned ones.
  const allLocked = selectedObjects.length > 0 && selectedObjects.every((o) => o.locked)
  const cursorList = Object.values(cursors)

  return (
    // Fills the space under the app header; the canvas itself must not scroll
    // the page.
    <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      {/* Board bar */}
      <div className="flex shrink-0 items-center gap-2 border-b bg-background px-3 py-2">
        <Button variant="ghost" size="icon-sm" asChild aria-label="Back to boards">
          <Link to="/boards">
            <ArrowLeft />
          </Link>
        </Button>
        <div className="min-w-0">
          {renaming === null ? (
            // A button rather than a div with an onClick: this is the only way
            // to rename a board, so it has to be reachable from the keyboard.
            <button
              type="button"
              onClick={() => setRenaming(board.title)}
              title="Rename this board"
              className="block max-w-[40vw] truncate rounded px-1 -mx-1 text-left text-sm font-semibold leading-tight hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {board.title}
            </button>
          ) : (
            <input
              autoFocus
              value={renaming}
              onChange={(ev) => setRenaming(ev.target.value)}
              // Blur saves, so clicking away from a finished edit keeps it.
              // Escape sets `renaming` to null first, and the blur that follows
              // then has nothing to save.
              onBlur={() => renaming !== null && void saveTitle(renaming)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') ev.currentTarget.blur()
                if (ev.key === 'Escape') setRenaming(null)
              }}
              maxLength={120}
              aria-label="Board name"
              className="-mx-1 block w-[40vw] max-w-sm rounded border bg-background px-1 text-sm font-semibold leading-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          )}
          <div className="truncate text-[11px] leading-tight text-muted-foreground">
            {currentTeam?.name} · {objects.length} {objects.length === 1 ? 'item' : 'items'}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {uploading && (
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Adding image…
            </span>
          )}
          {/* Kicking off a vote is a coach's job (0034 refuses it from anyone
              else), so the button only exists for one. Everybody else learns a
              vote has started from the banner. */}
          {isCoach && !voteOpen && (
            <Button variant="outline" size="sm" onClick={() => setVoteDialog(true)}>
              <VoteIcon /> Vote
            </Button>
          )}
          {cursorList.length > 0 && (
            <div className="flex -space-x-2">
              {cursorList.slice(0, 4).map((c) => (
                <Tooltip key={c.userId}>
                  <TooltipTrigger asChild>
                    <Avatar className="h-7 w-7 ring-2 ring-background">
                      <AvatarFallback className="text-[10px]">{initials(c.name)}</AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent>{c.name} is here</TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]',
                  live ? 'text-muted-foreground' : 'border-warning/40 text-warning',
                )}
              >
                {live ? <Radio className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                {live ? 'Live' : 'Offline'}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {live
                ? 'Edits sync with everyone on this board.'
                : 'Not connected to realtime — your changes still save, but you won’t see others’ live.'}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1">
        {/* Tool rail */}
        <div className="absolute left-3 top-3 z-20 flex flex-col gap-1 rounded-lg border bg-background/95 p-1 shadow-md backdrop-blur">
          {TOOLS.filter((t) => t.kind !== 'ping' || voteOpen).map((t) => (
            <Tooltip key={t.kind}>
              <TooltipTrigger asChild>
                <Button
                  variant={tool === t.kind ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => setTool(t.kind)}
                  aria-label={t.label}
                  aria-pressed={tool === t.kind}
                >
                  <t.icon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {t.label} <span className="ml-1 opacity-60">{t.key}</span>
                {'hint' in t && t.hint && (
                  <span className="ml-1.5 opacity-60">· {t.hint}</span>
                )}
              </TooltipContent>
            </Tooltip>
          ))}
          <Separator className="my-0.5" />
          <div className="grid grid-cols-2 gap-1 p-1">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => {
                  setColor(c)
                  // Recolours everything selected, which is the only reason to
                  // select five notes and then click a swatch.
                  for (const o of selectedObjects) if (!o.locked) void commit(o.id, { color: c })
                }}
                className={cn(
                  `note-${c}`,
                  'h-5 w-5 rounded border transition-transform hover:scale-110',
                  color === c ? 'ring-2 ring-ring ring-offset-1 ring-offset-background' : '',
                )}
                style={{ background: 'hsl(var(--note))' }}
                aria-label={`${c} color`}
                aria-pressed={color === c}
              />
            ))}
          </div>
        </div>

        {/* What the armed tool will do. The tool rail shows the active tool as a
            filled button, but that's a small cue in a narrow strip — and with
            the tool now staying armed between placements, "why is a shape
            appearing every time I click" needs answering as much as "why isn't
            anything appearing". */}
        {tool !== 'select' && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full border bg-background/95 px-3 py-1.5 text-xs shadow-md backdrop-blur">
            <span className="font-medium">{TOOLS.find((t) => t.kind === tool)?.label}</span>
            <span className="text-muted-foreground">
              {tool === 'pan'
                ? ' — drag to move the canvas'
                : tool === 'arrow'
                  ? ' — drag from one shape to another'
                  : tool === 'ping'
                    ? ' — click to drop a vote, click yours to take it back'
                    : ' — click to place, again for another'}
              {' · '}
              <Kbd>Esc</Kbd> to stop
            </span>
          </div>
        )}

        {/* Zoom controls */}
        <div className="absolute bottom-3 left-3 z-20 flex items-center gap-1 rounded-lg border bg-background/95 p-1 shadow-md backdrop-blur">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setView((v) => ({ ...v, zoom: clamp(v.zoom / 1.2, MIN_ZOOM, MAX_ZOOM) }))}
            aria-label="Zoom out"
          >
            <ZoomOut />
          </Button>
          <button
            className="min-w-[3.25rem] rounded px-1 text-xs tabular-nums hover:bg-accent"
            onClick={() => setView({ x: 0, y: 0, zoom: 1 })}
            title="Reset view"
          >
            {Math.round(view.zoom * 100)}%
          </button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setView((v) => ({ ...v, zoom: clamp(v.zoom * 1.2, MIN_ZOOM, MAX_ZOOM) }))}
            aria-label="Zoom in"
          >
            <ZoomIn />
          </Button>
        </div>

        {/* Selection actions */}
        {selectedObjects.length > 0 && (
          <div className="absolute right-3 top-3 z-20 flex items-center gap-1 rounded-lg border bg-background/95 p-1 shadow-md backdrop-blur">
            {selectedObjects.length > 1 && (
              <span className="px-1.5 text-xs tabular-nums text-muted-foreground">
                {selectedObjects.length} selected
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Restack in the order they already sit in, so a group keeps
                // its own front-to-back arrangement when it comes forward.
                let z = nextZ()
                for (const o of [...selectedObjects].filter((x) => !x.locked).sort((a, b) => a.z - b.z)) {
                  void commit(o.id, { z: z++ })
                }
              }}
              title="Bring to front"
            >
              Front
            </Button>
            <Separator orientation="vertical" className="h-5" />
            {/* Discoverable copy/paste. The keyboard is faster and is what most
                people will use, but nothing on screen advertises it — and a
                board is the one page where a visitor may be driving someone
                else's laptop. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={copySelection}
              title="Copy (⌘C) — paste on this board or another"
            >
              {copied > 0 ? 'Copied' : 'Copy'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void copySelectionAsImage()}
              disabled={imaging}
              title="Copy as image (⌘⇧C) — a PNG for a doc or a slide"
            >
              {imaging ? (
                <Loader2 className="animate-spin" />
              ) : (
                <ImageDown />
              )}
              {imaged === 'copied'
                ? 'Copied'
                : imaged === 'downloaded'
                  ? 'Saved'
                  : imaged === 'failed'
                    ? "Couldn't copy"
                    : 'Image'}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => void duplicateSelection()}
              aria-label="Duplicate selected"
              title="Duplicate (⌘D)"
            >
              <CopyPlus />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void setLocked(!allLocked)}
              aria-pressed={allLocked}
              title={allLocked ? 'Unlock (⌘⇧L) — anyone on the team can' : 'Lock (⌘⇧L) — stops it being moved, edited or deleted'}
            >
              {allLocked ? <LockOpen /> : <Lock />}
              {allLocked ? 'Unlock' : 'Lock'}
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => void removeSelection()}
              disabled={allLocked}
              aria-label="Delete selected"
              title={allLocked ? 'Locked — unlock to delete' : 'Delete'}
            >
              <Trash2 />
            </Button>
          </div>
        )}

        {/* A paste that did not work. Says so for six seconds and then gets out
            of the way: the alternative is a board where pasting a 20MB photo
            appears to do nothing at all. */}
        {notice && (
          // Floated top-centre: as an in-flow strip it sat under the tool rail,
          // which covered the start of every sentence it said.
          <div
            role="status"
            className="absolute left-1/2 top-3 z-30 max-w-md -translate-x-1/2 rounded-md border border-warning/50 bg-background px-3 py-1.5 text-xs shadow-md"
          >
            {notice}
          </div>
        )}

        {vote && (
          <VoteBanner
            vote={vote}
            used={dots.filter((d) => d.user_id === userId).length}
            total={dots.length}
            isCoach={isCoach}
            onEnd={() => void endVote()}
            onClear={() => void clearVote()}
          />
        )}

        {voteDialog && (
          <StartVoteDialog onClose={() => setVoteDialog(false)} onStart={startVote} />
        )}

        {/* Viewport */}
        <div
          ref={viewportRef}
          className={cn(
            'relative flex-1 touch-none overflow-hidden bg-muted/20',
            board.background === 'dots' && 'canvas-dots',
            board.background === 'grid' && 'canvas-grid',
            tool === 'pan' ? 'cursor-grab' : tool === 'select' ? 'cursor-default' : 'cursor-crosshair',
          )}
          onPointerDown={onViewportPointerDown}
          onPointerMove={onViewportPointerMove}
          onPointerUp={onViewportPointerUp}
          onWheel={onWheel}
        >
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}
          >
            {/* Frames first, regardless of z, so they act as backdrops rather
                than covering the shapes placed inside them. */}
            {[...objects]
              // Connected arrows are drawn by ConnectorLayer below: their line
              // comes from the two shapes they join, which a component holding
              // only its own row can't compute.
              .filter((o) => !(o.kind === 'arrow' && o.from_object_id && o.to_object_id))
              .sort((a, b) => (a.kind === 'frame' ? -1 : b.kind === 'frame' ? 1 : 0) || a.z - b.z)
              .map((o) => (
                <CanvasObject
                  key={o.id}
                  object={o}
                  imageUrl={imageUrls[imagePathOf(o.extra) ?? ''] ?? null}
                  zoom={view.zoom}
                  selected={selectedSet.has(o.id)}
                  editing={o.id === editingId}
                  interactive={tool === 'select'}
                  onSelect={(additive) => {
                    setSelectedIds((prev) => {
                      if (additive) {
                        return prev.includes(o.id)
                          ? prev.filter((sid) => sid !== o.id)
                          : [...prev, o.id]
                      }
                      // Clicking something already in the group KEEPS the group:
                      // otherwise grabbing one note to drag five collapses the
                      // selection to that note before the drag even starts.
                      return prev.includes(o.id) ? prev : [o.id]
                    })
                    setColor(o.color)
                  }}
                  onStartEdit={() => setEditingId(o.id)}
                  onEndEdit={(text) => {
                    setEditingId(null)
                    if (text === (o.text ?? '')) return
                    // Someone else locked it while this person was typing. The
                    // write would be refused (0070) and the reload would throw
                    // the text away, so hand the text back instead of losing it.
                    if (latest.current.objects.find((x) => x.id === o.id)?.locked) {
                      setNotice(`This item was locked while you were typing, so your text was not saved: “${text}”`)
                      return
                    }
                    void commit(o.id, { text: text || null })
                  }}
                  onDragStart={() => {
                    // Dragging one member of a selection drags the whole thing.
                    // Snapshot where everyone started; the deltas below are
                    // measured against these, never against the previous frame,
                    // so rounding cannot accumulate across a long drag.
                    const ids = selectedSet.has(o.id) && selectedIds.length > 1 ? selectedIds : [o.id]
                    const origin = new Map<string, { x: number; y: number }>()
                    for (const sid of ids) {
                      const t = objects.find((x) => x.id === sid)
                      // A locked member stays put while the rest of the
                      // selection moves past it; the database would refuse
                      // its move anyway (0070).
                      if (!t || t.locked) continue
                      origin.set(sid, { x: t.x, y: t.y })
                      draggingRef.current.add(sid)
                    }
                    groupDragRef.current = { anchorId: o.id, origin }
                  }}
                  onDrag={(patch) => {
                    const g = groupDragRef.current
                    const anchor = g?.origin.get(g.anchorId)
                    // Only a MOVE translates the group. A resize reports width
                    // and height, and resizing five objects from one handle is
                    // a different feature with its own maths.
                    if (g && anchor && g.origin.size > 1 && patch.x != null && patch.y != null) {
                      const dx = patch.x - anchor.x
                      const dy = patch.y - anchor.y
                      for (const [sid, from] of g.origin) {
                        const next = { x: from.x + dx, y: from.y + dy }
                        patchLocal(sid, next)
                        const live = latest.current.objects.find((x) => x.id === sid)
                        if (live) broadcastDrag({ ...live, ...next })
                      }
                      return
                    }
                    patchLocal(o.id, patch)
                    broadcastDrag({ ...o, ...patch })
                  }}
                  onDragEnd={(patch) => {
                    const g = groupDragRef.current
                    groupDragRef.current = null
                    const anchor = g?.origin.get(g.anchorId)
                    if (g && anchor && g.origin.size > 1 && patch.x != null && patch.y != null) {
                      const dx = patch.x - anchor.x
                      const dy = patch.y - anchor.y
                      for (const [sid, from] of g.origin) {
                        draggingRef.current.delete(sid)
                        void commit(sid, { x: from.x + dx, y: from.y + dy })
                      }
                      return
                    }
                    if (g) for (const sid of g.origin.keys()) draggingRef.current.delete(sid)
                    draggingRef.current.delete(o.id)
                    void commit(o.id, patch)
                  }}
                />
              ))}

            <ConnectorLayer
              objects={objects}
              selectedIds={selectedSet}
              interactive={tool === 'select'}
              zoom={view.zoom}
              drawing={drawing}
              onSelect={(cid, additive) =>
                setSelectedIds((prev) => {
                  if (additive) {
                    return prev.includes(cid) ? prev.filter((sid) => sid !== cid) : [...prev, cid]
                  }
                  return [cid]
                })
              }
            />

            {/* The rubber band, drawn in board space so it tracks the canvas
                rather than the screen while someone pans mid-drag. */}
            {marquee && <Marquee rect={normalizeRect(marquee.start, marquee.at)} />}

            {/* Vote dots, above the objects they sit on. Drawn at a constant
                SCREEN size (the 1/zoom scale) because a dot is a mark on the
                board, not an object with dimensions — shrinking it while zoomed
                out is what makes a busy result unreadable. */}
            {dots.map((d) => {
              const mine = d.user_id === userId
              return (
                <Tooltip key={d.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        // Foreground, not primary. A dot lands ON a sticky, and
                        // the note palette is built from the same DPL greens as
                        // `primary` — green dots on a green note read as faint
                        // rings. Foreground against a background ring is the one
                        // pair that holds over all seven colours in both themes.
                        'absolute h-4 w-4 rounded-full border-2 border-background shadow-sm',
                        mine ? 'bg-foreground ring-2 ring-primary' : 'bg-foreground/60',
                        mine && voteOpen ? 'cursor-pointer' : 'cursor-default',
                      )}
                      style={{
                        left: d.x,
                        top: d.y,
                        zIndex: 46,
                        transform: `translate(-50%, -50%) scale(${1 / view.zoom})`,
                      }}
                      onPointerDown={(e) => {
                        // Stop the canvas treating this as a click on bare
                        // board, which would start a marquee or drop a second
                        // dot right on top of this one.
                        e.stopPropagation()
                        if (mine && voteOpen) void removeDot(d.id)
                      }}
                      aria-label={mine ? 'Your vote' : 'A vote'}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    {mine
                      ? voteOpen
                        ? 'Your vote — click to take it back'
                        : 'Your vote'
                      : (voterNames[d.user_id] ?? 'A teammate')}
                  </TooltipContent>
                </Tooltip>
              )
            })}

            {/* The tally, once per object that has any dots. This is what
                `object_id` is for: dots scattered over a sticky are a count
                someone has to make by eye, and the eye gets it wrong at six. */}
            {tallies.map(({ object: o, count }) => (
              <div
                key={`tally-${o.id}`}
                className="pointer-events-none absolute rounded-full border border-background bg-primary px-1.5 text-[11px] font-semibold leading-5 text-primary-foreground shadow"
                style={{
                  left: o.x + o.width,
                  top: o.y,
                  zIndex: 47,
                  transform: `translate(-50%, -50%) scale(${1 / view.zoom})`,
                }}
              >
                {count}
              </div>
            ))}

            {/* Other people's cursors, drawn in board space so they land where
                that person actually is regardless of each viewer's pan/zoom. */}
            {cursorList.map((c) => (
              <div
                key={c.userId}
                className="pointer-events-none absolute z-50"
                style={{ left: c.x, top: c.y, transform: `scale(${1 / view.zoom})`, transformOrigin: 'top left' }}
              >
                <MousePointer2 className="h-4 w-4 fill-primary text-primary" />
                <span className="ml-3 -mt-1 inline-block whitespace-nowrap rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                  {c.name}
                </span>
              </div>
            ))}
          </div>

          {objects.length === 0 && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="rounded-lg border border-dashed bg-background/80 px-6 py-5 text-center text-sm text-muted-foreground backdrop-blur">
                <p className="font-medium text-foreground">Empty board</p>
                <p className="mt-1">
                  Press <Kbd>N</Kbd> and click to drop a sticky note.
                </p>
                <p className="mt-0.5">
                  Drag across the canvas to select several · <Kbd>⌘</Kbd><Kbd>D</Kbd> to duplicate them
                </p>
                <p className="mt-0.5">
                  Hold <Kbd>Space</Kbd> to pan · <Kbd>⌘</Kbd>+scroll to zoom
                </p>
                <p className="mt-0.5">
                  <Kbd>⌘C</Kbd> / <Kbd>⌘V</Kbd> to copy items between boards
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Every connected arrow on the board, plus the one currently being dragged.
 *
 * Drawn as a single SVG in board space rather than one element per arrow: the
 * lines have to be recomputed from live shape geometry on every render anyway,
 * and one layer keeps them all in a consistent paint order above the shapes.
 *
 * Sizing note: this svg spans a large fixed square centred on the board origin
 * rather than being sized to its contents. A zero-size svg is NOT an option —
 * `overflow: visible` does not rescue it, because a zero-width/height viewport
 * is never painted at all, so every connector silently vanished. Sizing it to
 * the board's actual bounds would mean recomputing those on every drag frame.
 *
 * The viewBox matches the CSS box exactly, so one user unit is one board unit
 * and children can be positioned in raw board coordinates. `overflow: visible`
 * stays as the backstop for anything dragged beyond the span.
 */
const SPAN = 40000
function ConnectorLayer({
  objects,
  selectedIds,
  interactive,
  zoom,
  drawing,
  onSelect,
}: {
  objects: BoardObject[]
  selectedIds: Set<string>
  interactive: boolean
  zoom: number
  drawing: { fromId: string | null; start: { x: number; y: number }; at: { x: number; y: number }; targetId: string | null } | null
  onSelect: (id: string, additive: boolean) => void
}) {
  const byId = React.useMemo(() => new Map(objects.map((o) => [o.id, o])), [objects])

  const connectors = objects.flatMap((o) => {
    if (o.kind !== 'arrow' || !o.from_object_id || !o.to_object_id) return []
    const from = byId.get(o.from_object_id)
    const to = byId.get(o.to_object_id)
    // An endpoint can be missing for a moment: the row arrives over realtime
    // before the shape it points at, or a delete lands out of order. Skipping
    // beats drawing a line to (0,0).
    if (!from || !to) return []
    return [{ o, ...connectorLine(from, to) }]
  })

  const target = drawing?.targetId ? byId.get(drawing.targetId) : null
  const source = drawing?.fromId ? byId.get(drawing.fromId) : null

  return (
    <svg
      className="pointer-events-none absolute overflow-visible"
      style={{ left: -SPAN / 2, top: -SPAN / 2, width: SPAN, height: SPAN, zIndex: 40 }}
      viewBox={`${-SPAN / 2} ${-SPAN / 2} ${SPAN} ${SPAN}`}
    >
      {connectors.map(({ o, a, b }) => {
        const selected = selectedIds.has(o.id)
        return (
          <g key={o.id} className={`note-${o.color}`}>
            {/* Invisible fat stroke so the line can actually be clicked — a 3px
                target is unhittable at low zoom. pointerEvents is set on the
                child because the svg itself opts out, letting clicks that miss
                the line fall through to the shapes underneath.

                Divided by zoom because this layer lives inside the scaled
                container: 14 board units would be 42 screen px at 3× (a fat
                invisible bar swallowing clicks meant for the shapes beneath)
                and 7px at 0.5×. Dividing keeps it ~14px on screen at any zoom. */}
            <line
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke="transparent"
              strokeWidth={14 / zoom}
              style={{ pointerEvents: interactive ? 'stroke' : 'none', cursor: 'pointer' }}
              onPointerDown={(e) => {
                e.stopPropagation()
                onSelect(o.id, e.shiftKey || e.metaKey)
              }}
            />
            <line
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke="hsl(var(--note))"
              strokeWidth={selected ? 4 : 3}
              strokeLinecap="round"
              markerEnd={`url(#arrowhead-${o.color})`}
            />
            {selected && (
              <>
                <circle cx={a.x} cy={a.y} r={4} fill="hsl(var(--primary))" />
                <circle cx={b.x} cy={b.y} r={4} fill="hsl(var(--primary))" />
              </>
            )}
          </g>
        )
      })}

      {/* The rubber-band line while dragging a new connector. */}
      {drawing && (
        <g className="text-primary">
          <line
            x1={source ? edgePoint(source, drawing.at).x : drawing.start.x}
            y1={source ? edgePoint(source, drawing.at).y : drawing.start.y}
            x2={target ? edgePoint(target, drawing.start).x : drawing.at.x}
            y2={target ? edgePoint(target, drawing.start).y : drawing.at.y}
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray="6 5"
          />
          {/* Ring the shape the arrow would land on, so it's obvious what
              releasing here would connect. */}
          {target && (
            <rect
              x={target.x - 4}
              y={target.y - 4}
              width={target.width + 8}
              height={target.height + 8}
              rx={target.kind === 'ellipse' ? Math.max(target.width, target.height) : 8}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            />
          )}
        </g>
      )}

      {/* One marker per palette colour. Markers can't inherit currentColor
          through markerEnd, so each is defined with its own class and the
          `note-<color>` variables resolved inside it. */}
      <defs>
        {COLORS.map((c) => (
          <marker
            key={c}
            id={`arrowhead-${c}`}
            viewBox="0 0 12 12"
            refX={10}
            refY={6}
            markerWidth={7}
            markerHeight={7}
            orient="auto-start-reverse"
            markerUnits="strokeWidth"
          >
            <path d="M 0 1 L 11 6 L 0 11 z" fill="hsl(var(--note))" className={`note-${c}`} />
          </marker>
        ))}
      </defs>
    </svg>
  )
}

/**
 * The rubber band itself.
 *
 * Lives inside the scaled container and is sized in board units, so it stays
 * pinned to the canvas if the view moves mid-drag, and its border thins out as
 * you zoom in rather than growing into a slab.
 */
function Marquee({ rect }: { rect: Rect }) {
  return (
    <div
      className="pointer-events-none absolute border border-primary bg-primary/10"
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, zIndex: 45 }}
    />
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium">
      {children}
    </kbd>
  )
}

interface CanvasObjectProps {
  object: BoardObject
  zoom: number
  selected: boolean
  editing: boolean
  interactive: boolean
  onSelect: (additive: boolean) => void
  onStartEdit: () => void
  onEndEdit: (text: string) => void
  onDragStart: () => void
  onDrag: (patch: Partial<BoardObject>) => void
  onDragEnd: (patch: Partial<BoardObject>) => void
  /** Signed URL for an image object; null until it is signed, or if it is gone. */
  imageUrl?: string | null
}

function CanvasObject({
  object: o,
  imageUrl,
  zoom,
  selected,
  editing,
  interactive,
  onSelect,
  onStartEdit,
  onEndEdit,
  onDragStart,
  onDrag,
  onDragEnd,
}: CanvasObjectProps) {
  // Drag/resize math lives in a ref: it changes every pointermove, and putting
  // it in state would re-render the whole board on each frame.
  const gesture = React.useRef<{
    mode: 'move' | 'resize'
    startX: number
    startY: number
    origin: { x: number; y: number; width: number; height: number }
  } | null>(null)

  function beginGesture(e: React.PointerEvent, mode: 'move' | 'resize') {
    if (!interactive || editing) return
    e.stopPropagation()
    onSelect(e.shiftKey || e.metaKey)
    // A locked item can be clicked — that is how it gets selected, and so how
    // it gets unlocked — but a press on it never becomes a move (0070).
    if (o.locked) return
    onDragStart()
    gesture.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { x: o.x, y: o.y, width: o.width, height: o.height },
    }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  function moveGesture(e: React.PointerEvent) {
    const g = gesture.current
    if (!g) return
    // Divide by zoom so a 10px pointer move is 10 board units at 100% and 20 at
    // 50% — the shape tracks the cursor exactly at any zoom level.
    const dx = (e.clientX - g.startX) / zoom
    const dy = (e.clientY - g.startY) / zoom
    if (g.mode === 'move') {
      onDrag({ x: g.origin.x + dx, y: g.origin.y + dy })
    } else if (o.kind === 'meeple' || o.kind === 'image') {
      // A meeple is a figure and an image is a photograph: neither survives a
      // free resize, which squashes one into a puddle and the other into a
      // funhouse mirror. The handle scales instead — width follows the pointer
      // and height keeps the ratio it was dropped with.
      const width = Math.max(32, g.origin.width + dx)
      onDrag({ width, height: (width * g.origin.height) / Math.max(g.origin.width, 1) })
    } else {
      onDrag({
        width: Math.max(40, g.origin.width + dx),
        height: Math.max(o.kind === 'arrow' ? 0 : 32, g.origin.height + dy),
      })
    }
  }

  function endGesture(e: React.PointerEvent) {
    const g = gesture.current
    if (!g) return
    gesture.current = null
    ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
    onDragEnd(
      g.mode === 'move' ? { x: o.x, y: o.y } : { width: o.width, height: o.height },
    )
  }

  const common = {
    onPointerDown: (e: React.PointerEvent) => beginGesture(e, 'move'),
    onPointerMove: moveGesture,
    onPointerUp: endGesture,
    onDoubleClick: (e: React.MouseEvent) => {
      e.stopPropagation()
      if (o.kind !== 'arrow' && !o.locked) onStartEdit()
    },
  }

  const ring = selected ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''

  if (o.kind === 'arrow') {
    // An arrow is defined by width/height as a vector from (x, y), so it can
    // point in any direction without a rotation transform.
    const len = Math.max(Math.hypot(o.width, o.height), 8)
    const angle = (Math.atan2(o.height, o.width) * 180) / Math.PI
    return (
      <div
        className={cn('absolute', interactive && !o.locked && 'cursor-move')}
        style={{ left: o.x, top: o.y, width: len, height: 24, transform: `rotate(${angle}deg)`, transformOrigin: '0 50%', zIndex: o.z }}
        {...common}
      >
        <svg width={len} height={24} className={cn('overflow-visible', ring)}>
          <line
            x1={0}
            y1={12}
            x2={len - 8}
            y2={12}
            stroke="hsl(var(--note))"
            strokeWidth={3}
            strokeLinecap="round"
            className={`note-${o.color}`}
          />
          <polygon
            points={`${len},12 ${len - 12},6 ${len - 12},18`}
            fill="hsl(var(--note))"
            className={`note-${o.color}`}
          />
        </svg>
        {o.locked && <LockBadge zoom={zoom} />}
        {selected && interactive && !o.locked && (
          <ResizeHandle onPointerDown={(e) => beginGesture(e, 'resize')} onPointerMove={moveGesture} onPointerUp={endGesture} />
        )}
      </div>
    )
  }

  if (o.kind === 'meeple') {
    return (
      <div
        className={cn('absolute select-none rounded-md', `note-${o.color}`, ring, interactive && !editing && !o.locked && 'cursor-move')}
        style={{ left: o.x, top: o.y, width: o.width, height: o.height, zIndex: o.z }}
        {...common}
      >
        <MeepleFigure />
        {/* The name sits BELOW the token rather than on it. A meeple is small by
            default and shaped like a person, so text laid over it is unreadable
            at any size worth dropping — and a row of tokens reads as a row of
            labelled people only if the labels line up under them. */}
        <div className="absolute -bottom-6 left-1/2 w-[190%] -translate-x-1/2 text-center">
          {editing ? (
            <TextEditor
              value={o.text ?? ''}
              className="w-full rounded border bg-background px-1 text-center text-xs font-medium"
              onDone={onEndEdit}
            />
          ) : (
            <span
              className={cn(
                'block break-words text-xs font-medium leading-tight',
                o.text ? 'text-foreground' : 'text-muted-foreground opacity-60',
              )}
            >
              {o.text || 'Double-click to name'}
            </span>
          )}
        </div>
        {o.locked && <LockBadge zoom={zoom} />}
        {selected && interactive && !o.locked && (
          <ResizeHandle onPointerDown={(e) => beginGesture(e, 'resize')} onPointerMove={moveGesture} onPointerUp={endGesture} />
        )}
      </div>
    )
  }

  if (o.kind === 'image') {
    return (
      <div
        className={cn(
          'absolute select-none overflow-hidden rounded-sm bg-muted shadow-md',
          ring,
          interactive && !editing && !o.locked && 'cursor-move',
        )}
        style={{ left: o.x, top: o.y, width: o.width, height: o.height, zIndex: o.z }}
        {...common}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={o.text ?? 'Pasted image'}
            // `draggable` off, or the browser's own image drag competes with
            // the canvas gesture and the object jumps back on release.
            draggable={false}
            className="pointer-events-none h-full w-full object-contain"
          />
        ) : (
          // Shown while the URL is being signed, and left in place for an image
          // whose file has gone. Either way there is a rectangle to select and
          // delete, which a zero-sized placeholder would not give you.
          <div className="flex h-full w-full items-center justify-center text-[11px] text-muted-foreground">
            <ImageIcon className="h-4 w-4 opacity-60" />
          </div>
        )}
        {o.locked && <LockBadge zoom={zoom} />}
        {selected && interactive && !o.locked && (
          <ResizeHandle onPointerDown={(e) => beginGesture(e, 'resize')} onPointerMove={moveGesture} onPointerUp={endGesture} />
        )}
      </div>
    )
  }

  if (o.kind === 'frame') {
    return (
      <div
        className={cn('absolute rounded-lg border-2 border-dashed', ring, interactive && !o.locked && 'cursor-move')}
        style={{ left: o.x, top: o.y, width: o.width, height: o.height, zIndex: 0, borderColor: 'hsl(var(--muted-foreground) / 0.5)' }}
        {...common}
      >
        <div className="absolute -top-6 left-0 max-w-full">
          {editing ? (
            <TextEditor
              value={o.text ?? ''}
              className="rounded border bg-background px-1 text-xs font-medium"
              onDone={onEndEdit}
            />
          ) : (
            <span className="truncate text-xs font-medium text-muted-foreground">
              {o.text || 'Frame'}
            </span>
          )}
        </div>
        {o.locked && <LockBadge zoom={zoom} />}
        {selected && interactive && !o.locked && (
          <ResizeHandle onPointerDown={(e) => beginGesture(e, 'resize')} onPointerMove={moveGesture} onPointerUp={endGesture} />
        )}
      </div>
    )
  }

  const isText = o.kind === 'text'
  return (
    <div
      className={cn(
        'absolute select-none',
        `note-${o.color}`,
        o.kind === 'note' && 'rounded-sm shadow-md',
        o.kind === 'rect' && 'rounded-md',
        o.kind === 'ellipse' && 'rounded-[50%]',
        ring,
        interactive && !editing && !o.locked && 'cursor-move',
      )}
      style={{
        left: o.x,
        top: o.y,
        width: o.width,
        height: o.height,
        zIndex: o.z,
        background: isText ? 'transparent' : 'hsl(var(--note))',
        color: isText ? undefined : 'hsl(var(--note-ink))',
      }}
      {...common}
    >
      <div
        className={cn(
          'flex h-full w-full overflow-hidden p-2.5',
          o.kind === 'ellipse' ? 'items-center justify-center text-center' : 'items-start',
          isText && 'items-center p-1 font-medium',
        )}
        style={{ fontSize: o.font_size }}
      >
        {editing ? (
          <TextEditor
            value={o.text ?? ''}
            className={cn('h-full w-full resize-none bg-transparent outline-none', o.kind === 'ellipse' && 'text-center')}
            multiline
            onDone={onEndEdit}
          />
        ) : (
          <span className="whitespace-pre-wrap break-words leading-snug">
            {o.text || (
              // Only the kinds that exist to hold words prompt for them. A rect
              // or ellipse is just as often a backdrop — a region on a canvas, a
              // container for other shapes — and a big empty box begging to be
              // typed into is noise, most visibly on a template where every
              // structural shape carried the same placeholder. Double-clicking
              // still adds text to any of them.
              (o.kind === 'note' || isText) && (
                <span className="opacity-40">{isText ? 'Text' : 'Double-click to type'}</span>
              )
            )}
          </span>
        )}
      </div>
      {o.locked && <LockBadge zoom={zoom} />}
      {selected && interactive && !o.locked && (
        <ResizeHandle onPointerDown={(e) => beginGesture(e, 'resize')} onPointerMove={moveGesture} onPointerUp={endGesture} />
      )}
    </div>
  )
}

/**
 * The token: a head and a body, filled with the object's palette color.
 *
 * Inline SVG rather than an icon component so it stretches to whatever box the
 * meeple has been resized to and inherits `--note` from the `note-<color>`
 * class on its wrapper — which is what makes recoloring a meeple the same
 * palette click as recoloring a sticky note, in both themes.
 *
 * The thin `--note-ink` outline is load-bearing rather than decorative: two
 * meeples of the same color standing next to each other, or one standing on a
 * rect of its own color, otherwise merge into a single blob.
 */
function MeepleFigure() {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" className="h-full w-full overflow-visible">
      <g fill="hsl(var(--note))" stroke="hsl(var(--note-ink) / 0.45)" strokeWidth={2.5}>
        <circle cx={50} cy={19} r={15} />
        <path d="M50 35 C62 35 71 43 75 53 L90 65 C95 69 93 77 86 77 L71 77 C67 77 64 75 62 71 C64 82 66 91 67 96 C67 99 65 100 62 100 L38 100 C35 100 33 99 33 96 C34 91 36 82 38 71 C36 75 33 77 29 77 L14 77 C7 77 5 69 10 65 L25 53 C29 43 38 35 50 35 Z" />
      </g>
    </svg>
  )
}

/**
 * The mark on a locked item (0070): a small padlock on the top-right corner.
 * Drawn at a constant SCREEN size, like vote dots, so it stays readable when
 * zoomed out and does not swallow a sticky note when zoomed in. Always shown,
 * not only when selected — the point is to know before you try to drag it.
 */
function LockBadge({ zoom }: { zoom: number }) {
  return (
    <span
      className="pointer-events-none absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm"
      style={{ transform: `scale(${1 / zoom})`, transformOrigin: 'top right', zIndex: 1 }}
      aria-label="Locked"
    >
      <Lock className="h-3 w-3" />
    </span>
  )
}

function ResizeHandle(props: {
  onPointerDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: (e: React.PointerEvent) => void
}) {
  return (
    <div
      className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-se-resize rounded-sm border-2 border-primary bg-background"
      {...props}
      aria-label="Resize"
    />
  )
}

/**
 * Inline text editing. Commits on blur or Escape rather than per keystroke, so
 * typing a note is one database write instead of dozens.
 */
function TextEditor({
  value,
  className,
  multiline,
  onDone,
}: {
  value: string
  className?: string
  multiline?: boolean
  onDone: (text: string) => void
}) {
  const [draft, setDraft] = React.useState(value)
  const ref = React.useRef<HTMLTextAreaElement | HTMLInputElement>(null)

  // Commit exactly once, from whichever path gets there first.
  //
  // The important path is the UNMOUNT one. Clicking the canvas clears the
  // board's `editingId`, which tears this component down — and a React `onBlur`
  // does not fire for an element that has already been removed, so the typed
  // text used to be dropped on the floor. Committing from the cleanup means no
  // exit can lose an edit, whoever closed the editor and in what order.
  const committed = React.useRef(false)
  const draftRef = React.useRef(draft)
  draftRef.current = draft
  const onDoneRef = React.useRef(onDone)
  onDoneRef.current = onDone

  const initial = React.useRef(value)

  const finish = React.useCallback(() => {
    if (committed.current) return
    committed.current = true
    onDoneRef.current(draftRef.current.trim())
  }, [])

  React.useEffect(() => {
    committed.current = false
    ref.current?.focus()
    if (ref.current && 'select' in ref.current) ref.current.select()

    return () => {
      // Only rescue a REAL edit. React StrictMode double-invokes effects in dev
      // (mount → cleanup → mount), so an unconditional commit here would fire on
      // mount, close the editor immediately, and latch `committed` — turning dev
      // into a worse version of the bug this is fixing.
      if (draftRef.current.trim() !== initial.current.trim()) finish()
    }
  }, [finish])

  const shared = {
    value: draft,
    className,
    // Stop the canvas from treating typing as a drag or a tool shortcut.
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onBlur: finish,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        finish()
      }
      if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        finish()
      }
    },
  }

  return multiline ? (
    <textarea
      {...shared}
      ref={ref as React.RefObject<HTMLTextAreaElement>}
      onChange={(e) => setDraft(e.target.value)}
    />
  ) : (
    <input
      {...shared}
      ref={ref as React.RefObject<HTMLInputElement>}
      onChange={(e) => setDraft(e.target.value)}
    />
  )
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n))
}
